import type {
    MODULE_CONTENT_KEY,
    StepType,
    KeywordGenerationMessage,
    PatentEvaluationMessage,
} from '.';

/**
 * 事件类型
 */
export enum SSE_EVENT_TYPE {
    /**
     * 初始欢迎消息
     */
    WELCOME = 'WELCOME_MESSAGE',
    /**
     * 用户发送消息
     */
    USER = 'CHAT_MESSAGE_USER',
    /**
     * 标题消息
     */
    TITLE = 'TITLE_MESSAGE',
    /**
     * 回复卡片标题消息
     */
    AGENT_TITLE = 'AGENT_TITLE_MESSAGE',
    /**
     * 回复消息
     */
    AGENT = 'CHAT_MESSAGE_AGENT',
    /**
     * 需要用户确认问题
     */
    CONFIRM = 'NEED_USER_CONFIRM',
    /**
     * 自动确认问题
     */
    AGENT_CONFIRM = 'AGENT_CONFIRM',
    /**
     * 用户确认消息
     */
    USER_CONFIRM = 'USER_CONFIRM',
    /**
      * 计划消息
      */
    PLAN = 'PLAN_MESSAGE',
    /**
      * 模块内容思考中消息
      */
    MODULE_CONTENT_THINK = 'MODULE_CONTENT_THINK',
    /**
     * 内容消息
     */
    MODULE_CONTENT = 'MODULE_CONTENT',
    /**
     * 版本控制消息
     */
    VERSION_CREATED = 'VERSION_CREATED',
    /**
     * 任务开始
     */
    TASK_START = 'TASK_START',
    NODE_START = 'NODE_START',
    /**
     * 任务结束
     */
    TASK_END = 'TASK_END',
    /**
     * 错误消息
     */
    ERROR = 'ERROR',
    /**
     * 融合数据（目前只给step3 extend使用）
     */
    MODULE_CONTENT_MERGE = 'MODULE_CONTENT_MERGE',
    /**
     * 模块结束
     */
    NODE_END = 'NODE_END',
    RAG_RECALL_PROCESS = 'RAG_RECALL_PROCESS',
    /**
     * 中断
     */
    INTERRUPTED = 'INTERRUPTED'
}

/**
 * 初始欢迎消息
 */
export interface WelcomeMessageData {
    id: number;
    business_id: string;
    data: {
        message_type: SSE_EVENT_TYPE.WELCOME;
    }
}

/**
 * 用户发送消息
 */
export interface UserMessageData {
    id: number;
    business_id: string;
    data: {
        message_type: SSE_EVENT_TYPE.USER;
        message: {
            text: string;
            upload_files?: {
                file_name: string;
                file_type: string;
                record_id: string;
                object_key: string;
            }[];
        };
        reference_content?: {
            reference_title: string
        },
    }
}

/**
 * 用户确认消息
 */
export interface UserConfirmMessageData {
    id: number;
    business_id: string;
    data: {
        message_type: SSE_EVENT_TYPE.USER_CONFIRM;
        message?: {
            problems: {
                problem_description: string,
                select: boolean
            }[]
        },
        step: StepType;
        reference_content?: {
            reference_title: string
        },
    }
}

/**
 * 标题消息
 */
export interface TitleMessageData {
    id: number;
    business_id: string;
    data: {
        message_type: SSE_EVENT_TYPE.TITLE;
        message: string;
    }
}

/**
 * 回复卡片标题消息
 */
export interface AgentTitleMessageData {
    id: number;
    business_id: string;
    data: {
        message_type: SSE_EVENT_TYPE.AGENT_TITLE;
        message: string;
        message_group: string;
    }
}

/**
 * Agent回复消息
 */
export interface AgentMessageData {
    id: number;
    business_id: string;
    data: {
        message_type: SSE_EVENT_TYPE.AGENT;
        message: string;
        message_group?: string;
    }
}

/**
 * RAG 召回过程消息数据 - 召回完成
 */
export interface RagRecallFinishMessageData {
    id: number;
    business_id: string;
    data: {
        message_type: SSE_EVENT_TYPE.RAG_RECALL_PROCESS;
        message: {
            rag_recall_finish: true;
        };
        message_group: string;
        role: string;
    };
}

/**
 * 需要用户确认问题
 */
export interface ConfirmMessageData {
    id: number;
    business_id: string;
    data: {
        message_type: SSE_EVENT_TYPE.CONFIRM;
        step: StepType;
        message?: {
            problems?: {
                select: boolean;
                problem_id: string;
                problem_description: string;
                user_friendly_description: string;
            }[];
        }
        version_number: number;
        message_group: string;
    }
}

/**
 * Think消息
 */
export interface ThinkMessageData {
    id: number;
    business_id: string;
    data: {
        message_type: SSE_EVENT_TYPE.MODULE_CONTENT_THINK;
        module_key: MODULE_CONTENT_KEY;
        /**
         * 增量消息
         */
        stream_message?: string;
        /**
        * 全量消息
        */
        message?: string
        version_number: number;
        message_group: string;
        step?: number;
    }
}

/**
 * Step 消息数据体
 */
export interface ModuleContentMessageData {
    id: number;
    business_id: string;
    data: {
        message_type: SSE_EVENT_TYPE.MODULE_CONTENT;
        message: any;
        module_key: MODULE_CONTENT_KEY;
        step: number;
        version_number: number;
        role: string;
    }
}

/**
 * agent confirm 消息数据体
 */
export interface AgentConfirmMessageData {
    id: number;
    business_id: string;
    data: {
        message_type: SSE_EVENT_TYPE.AGENT_CONFIRM;
        message: any;
        message_group: string;
        step: number;
        version_number: number;
        role: string;
    }
}

/**
 * Step Merge 消息数据体
 */
export interface ModuleContentMergeMessageData {
    id: number;
    business_id: string;
    data: {
        message_type: SSE_EVENT_TYPE.MODULE_CONTENT_MERGE;
        message: any;
        module_key: MODULE_CONTENT_KEY;
        step: number;
        version_number: number;
        role: string;
    }
}

/**
 * Step Merge 消息数据体
 */
export interface NodeEndMessageData {
    id: number;
    business_id: string;
    data: {
        message_type: SSE_EVENT_TYPE.NODE_END;
        message: MODULE_CONTENT_KEY;
        step: number;
        version_number: number;
        role: string;
    }
}

/**
 * 版本创建消息
 */
export interface VersionCreatedMessageData {
    id: number;
    business_id: string;
    data: {
        message_type: SSE_EVENT_TYPE.VERSION_CREATED;
        version_number: number;
    }
}

/**
 * 任务开始消息
 */
export interface TaskStartMessageData {
    id: number;
    business_id: string;
    data: {
        message_type: SSE_EVENT_TYPE.TASK_START;
    };
}

export interface NodeStartMessageData {
    id: number;
    business_id: string;
    data: {
        message_type: SSE_EVENT_TYPE.NODE_START;
        message: MODULE_CONTENT_KEY;
        step: number;
        version_number: number;
        role: string;
        message_group: string;
    };
}

/**
 * 任务结束消息
 */
export interface TaskEndMessageData {
    id: number;
    business_id: string;
    data: {
        message_type: SSE_EVENT_TYPE.TASK_END;
    };
}

/**
 * 计划消息
 */
export interface PlanMessageData {
    id: number;
    business_id: string;
    data: {
        message_type: SSE_EVENT_TYPE.PLAN;
    };
}

/**
 * 专利召回 - 关键词生成消息数据
 */
export interface PatentRecallKeywordGenerationMessageData {
    id: number;
    business_id: string;
    data: {
        message_type: SSE_EVENT_TYPE.MODULE_CONTENT_MERGE;
        message: KeywordGenerationMessage;
        module_key: MODULE_CONTENT_KEY.BATCH_PATENT_RECALL;
        step: number;
        version_number: number;
        message_group: string;
    };
}

/**
 * 专利召回 - 专利评估消息数据
 */
export interface PatentRecallEvaluationMessageData {
    id: number;
    business_id: string;
    data: {
        message_type: SSE_EVENT_TYPE.MODULE_CONTENT_MERGE;
        message: PatentEvaluationMessage;
        module_key: MODULE_CONTENT_KEY.BATCH_PATENT_RECALL;
        step: number;
        version_number: number;
        message_group: string;
    };
}

/**
 * 任务中断消息
 */
export interface InterruptedMessageData {
    id: number;
    business_id: string;
    type: string;
    phase: string;
    data: {
        role: string;
        message_type: SSE_EVENT_TYPE.INTERRUPTED;
    };
}

export type EventData = WelcomeMessageData |
    UserMessageData |
    UserConfirmMessageData |
    TitleMessageData |
    AgentTitleMessageData |
    AgentMessageData |
    ConfirmMessageData |
    ThinkMessageData |
    ModuleContentMessageData |
    VersionCreatedMessageData |
    TaskStartMessageData |
    NodeStartMessageData |
    TaskEndMessageData |
    PlanMessageData |
    ModuleContentMergeMessageData |
    NodeEndMessageData |
    PatentRecallKeywordGenerationMessageData |
    PatentRecallEvaluationMessageData |
    AgentConfirmMessageData |
    RagRecallFinishMessageData |
    InterruptedMessageData;
